import { signal } from "@preact/signals-core";
import { msg } from "@lit/localize";
import { fetchProjects } from "@/store/projects";
import { showToast } from "@/components/ui/toast-store";
import { apiErrorMessage } from "@/lib/api-errors";
import {
  getApps,
  getAppsBySlugBySlug,
  postApps,
  postAppsByIdProjects,
} from "@/api";
import type {
  DtoAppResponse,
  DtoCreateAppRequest,
  DtoCreateProjectRequest,
} from "@/api";

export interface AppsState {
  apps: DtoAppResponse[];
  isLoading: boolean;
}

export const apps = signal<AppsState>({ apps: [], isLoading: true });

export async function fetchApps(): Promise<void> {
  apps.value = { ...apps.value, isLoading: true };
  try {
    const { data } = await getApps({ throwOnError: true });
    apps.value = { apps: data ?? [], isLoading: false };
  } catch {
    apps.value = { apps: [], isLoading: false };
    showToast(msg("Failed to load apps"), { variant: "error" });
  }
}

export interface AppDetailState {
  app: DtoAppResponse | null;
  isLoading: boolean;
}

export const appDetail = signal<AppDetailState>({
  app: null,
  isLoading: false,
});

export async function fetchAppBySlug(slug: string): Promise<void> {
  appDetail.value = { app: null, isLoading: true };
  try {
    const { data } = await getAppsBySlugBySlug({
      path: { slug },
      throwOnError: true,
    });
    appDetail.value = { app: data ?? null, isLoading: false };
  } catch {
    appDetail.value = { app: null, isLoading: false };
    showToast(msg("Failed to load app"), { variant: "error" });
  }
}

/** Creates an app. Throws an Error whose message says why it failed. */
export async function createApp(
  body: DtoCreateAppRequest,
): Promise<DtoAppResponse> {
  try {
    const { data } = await postApps({ body, throwOnError: true });
    await fetchApps();
    return data;
  } catch (err) {
    throw new Error(apiErrorMessage(err, {
      forbidden: msg(
        "You don't have permission to create apps. Ask a workspace owner or admin.",
      ),
      fallback: msg("Couldn't create the app. Please try again."),
    }));
  }
}

/** Creates a project in an app. Throws an Error saying why it failed. */
export async function createProjectInApp(
  appId: string,
  body: DtoCreateProjectRequest,
): Promise<void> {
  try {
    await postAppsByIdProjects({
      path: { id: appId },
      body,
      throwOnError: true,
    });
    await fetchApps();
    await fetchProjects();
    showToast(msg("Project created"), { variant: "success" });
  } catch (err) {
    throw new Error(apiErrorMessage(err, {
      forbidden: msg(
        "You don't have permission to add projects to this app. Ask a workspace owner or admin.",
      ),
      fallback: msg("Couldn't create the project. Please try again."),
    }));
  }
}
